import type {Artifact} from './artifact-core'

const PREVIEWABLE_LANGUAGE_TIERS = ['html', 'markdown', 'mermaid'] as const
const HTML_ENTRY_POINT = 'index.html'

// `pending` and `ambiguous` are kept apart because callers have to treat them
// differently. A pending turn may still gain the artifact that decides it, so it
// stays eligible. An ambiguous one never will, so its decision is final and it has
// to be recorded as such, or a later artifact update could reopen the question long
// after the user moved on.
export type DefaultArtifactSelection =
  | {status: 'selected'; artifact: Artifact}
  | {status: 'pending'}
  | {status: 'ambiguous'}

export function selectDefaultArtifact(artifacts: readonly Artifact[]): DefaultArtifactSelection {
  const completeArtifacts = artifacts.filter(artifact => artifact.content !== undefined)
  const [onlyArtifact] = completeArtifacts
  if (!onlyArtifact) return {status: 'pending'}
  if (completeArtifacts.length === 1) return {status: 'selected', artifact: onlyArtifact}

  for (const language of PREVIEWABLE_LANGUAGE_TIERS) {
    const tier = completeArtifacts.filter(artifact => artifact.language === language)
    const [onlyInTier] = tier
    if (!onlyInTier) continue

    if (language === 'html') {
      const entryPoints = tier.filter(artifact => artifact.fileName.toLowerCase() === HTML_ENTRY_POINT)
      const [onlyEntryPoint] = entryPoints
      if (entryPoints.length === 1 && onlyEntryPoint) return {status: 'selected', artifact: onlyEntryPoint}
    }

    return tier.length === 1 ? {status: 'selected', artifact: onlyInTier} : {status: 'ambiguous'}
  }

  return {status: 'ambiguous'}
}
